import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { queryKeys } from '@/lib/api/queryKeys';
import { despliegueDeEstacion, xurveyApi } from '@/lib/api/xurvey';
import { useT } from '@/lib/i18n/useT';

interface ResponsesTabProps {
  stationId: number;
  surveyId: number | null;
}

const PAGE_SIZE = 20;

export function ResponsesTab({ stationId, surveyId }: ResponsesTabProps) {
  const t = useT();
  const [page, setPage] = useState(1);

  /*
   * Las respuestas se piden por DESPLIEGUE, no por estación.
   *
   * Una respuesta pertenece al enlace del que vino, no a la entidad: es lo que
   * permite que Xurvey no necesite una columna por cada aplicación del
   * ecosistema. Así que primero hay que saber cuál es el despliegue de esta
   * estación, y eso es una consulta más — cacheada, y la misma que ya usa la
   * pestaña de encuesta del evento.
   */
  const despliegues = useQuery({
    queryKey: ['xurvey', 'deployments', surveyId],
    queryFn: () => xurveyApi.listDeployments(surveyId!),
    enabled: !!surveyId,
    staleTime: 60_000,
  });
  // El real y activo: las respuestas de un QR de prueba no son respuestas.
  const publicLinkId = despliegueDeEstacion(despliegues.data, stationId)?.id;

  const query = useQuery({
    queryKey: queryKeys.stations.responses(stationId, page),
    queryFn: () =>
      xurveyApi.listResponses(surveyId!, { publicLinkId, page, limit: PAGE_SIZE }),
    // Sin despliegue no hay nada que pedir: pedirlo sin filtro devolvería las
    // respuestas de TODAS las estaciones de la encuesta bajo el título de
    // ésta, que es peor que no enseñar nada.
    enabled: !!surveyId && !!publicLinkId,
  });

  if (!surveyId) {
    return (
      <Card>
        <CardContent>
          <p className="py-12 text-center text-sm text-[var(--color-fg-muted)]">
            {t('stations.detail.noSurveyAssigned')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (despliegues.isLoading || (query.isLoading && !!publicLinkId)) {
    return (
      <Card>
        <CardContent className="space-y-2 py-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  const items = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // La etiqueta de cada respuesta vive en las columnas, no en la fila: la
  // pregunta es de la encuesta y se repite en todas, así que viaja una vez.
  const columnas = query.data?.columns ?? [];

  if (items.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className="py-12 text-center text-sm text-[var(--color-fg-muted)]">
            {t('stations.detail.noResponses')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('stations.detail.responses')}{' '}
          <span className="text-sm font-normal text-[var(--color-fg-muted)]">
            ({total})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
              <tr>
                <th className="py-2 text-left">
                  {t('stations.detail.responseTimestamp')}
                </th>
                <th className="py-2 text-left">
                  {t('stations.detail.responseSummary')}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                // Las dos primeras respuestas, como vistazo. Se recorren las
                // columnas y no los valores para que el orden sea el de la
                // encuesta y no el que devuelva el objeto.
                const preview = columnas
                  .map((c) => ({ label: c.label, value: row.values[c.componentId] }))
                  .filter((a) => a.value)
                  .slice(0, 2)
                  .map((a) => `${a.label}: ${a.value}`)
                  .join(' · ');
                return (
                  <tr
                    key={row.responseId}
                    className="border-t border-[var(--color-border-subtle)]"
                  >
                    <td className="py-3 align-top tabular-nums text-[var(--color-fg-muted)]">
                      {row.completedAt
                        ? new Date(row.completedAt).toLocaleString()
                        : '—'}
                    </td>
                    <td className="py-3 text-[var(--color-fg-primary)]">
                      {preview || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-end gap-2">
            <span className="text-xs text-[var(--color-fg-muted)]">
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label={t('stations.detail.prevPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label={t('stations.detail.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
